import { useState } from 'react'
import { useAsync } from '../hooks/useAsync'
import { errorMessage } from '../services/api'
import { fetchPlayerAliases, removePlayerAlias } from '../services/league'
import {
  Card,
  EmptyState,
  Loading,
  Notice,
  PlayerCell,
  SortableHeaderRow,
  useTableSort,
  type SortableColumn,
} from './ui'
import type { LeaguePlayerAliases } from '../types'

/**
 * Admin tooling for the alternate spellings imports resolve through.
 *
 * @remarks
 * Aliases are input spellings rather than names the site renders, and nothing
 * else in the app shows them as data — they arrive silently from a workbook
 * import or a merge and are otherwise only visible as a profile footnote.
 * Removing one is the only destructive action here, so it is confirmed the way
 * a merge is: a card that states the consequence before anything is written.
 */

/** One alias awaiting confirmation, with the player it belongs to. */
interface PendingRemoval {
  player: LeaguePlayerAliases
  alias: string
}

const ALIAS_COLUMNS: Array<SortableColumn<LeaguePlayerAliases>> = [
  { key: 'player', label: 'Player', sort: row => row.displayName },
  {
    key: 'count',
    label: '#',
    className: 'num',
    sortLabel: 'alias count',
    hint: 'Sort by how many spellings each player answers to',
    sort: row => row.aliases.length,
  },
  { key: 'aliases', label: 'Spellings that resolve here', sort: row => row.aliases.join(', ') },
]

function AliasTable({
  rows,
  onRemove,
}: {
  rows: LeaguePlayerAliases[]
  onRemove: (removal: PendingRemoval) => void
}) {
  const sort = useTableSort(rows, ALIAS_COLUMNS)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={ALIAS_COLUMNS} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(row => (
            <tr key={row.leaguePlayerId}>
              <td>
                <PlayerCell
                  name={row.displayName}
                  discordId={row.discordId}
                  avatar={row.discordAvatar}
                />
              </td>
              <td className="num">{row.aliases.length}</td>
              <td>
                <div className="row">
                  {row.aliases.map(alias => (
                    <span key={alias} className="row" style={{ gap: 6 }}>
                      <code>{alias}</code>
                      <button
                        type="button"
                        className="btn btn--danger btn--sm"
                        title={`Remove "${alias}" from ${row.displayName}`}
                        onClick={() => onRemove({ player: row, alias })}
                      >
                        Remove
                      </button>
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** The confirm step: what removing this one spelling costs. */
function RemovalCard({
  removal,
  onCancel,
  onDone,
}: {
  removal: PendingRemoval
  onCancel: () => void
  onDone: (removal: PendingRemoval) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function confirm(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      await removePlayerAlias(removal.player.leaguePlayerId, removal.alias)
      onDone(removal)
    } catch (caught) {
      setError(errorMessage(caught))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      title="Remove this alias?"
      subtitle="Nothing has been written yet, and nothing in the app can add an alias back."
      actions={
        <div className="row">
          <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--danger btn--sm"
            disabled={busy}
            onClick={() => void confirm()}
          >
            {busy ? 'Removing…' : `Remove ${removal.alias}`}
          </button>
        </div>
      }
    >
      <div className="stack-sm">
        {error && <Notice tone="error">{error}</Notice>}
        <p>
          A workbook that spells <strong>{removal.player.displayName}</strong> as{' '}
          <code>{removal.alias}</code> currently imports into their record. Once this alias is
          gone, that spelling imports as a new player instead, which is then a merge to clean up.
        </p>
      </div>
    </Card>
  )
}

export default function PlayerAliasPanel() {
  const aliases = useAsync(fetchPlayerAliases, [])
  const [pending, setPending] = useState<PendingRemoval | null>(null)
  const [removed, setRemoved] = useState<PendingRemoval | null>(null)

  function finish(removal: PendingRemoval): void {
    setPending(null)
    setRemoved(removal)
    aliases.reload()
  }

  const rows = aliases.data ?? []

  return (
    <div className="stack">
      <Notice>
        Removing an alias means a future import of a workbook using that spelling will create a
        new duplicate player instead of resolving to the existing one.
      </Notice>

      {aliases.error && <Notice tone="error">{aliases.error}</Notice>}

      {removed && (
        <Notice tone="success">
          Removed <code>{removed.alias}</code> from <strong>{removed.player.displayName}</strong>.
        </Notice>
      )}

      {pending && (
        <RemovalCard removal={pending} onCancel={() => setPending(null)} onDone={finish} />
      )}

      <Card
        title="Recorded aliases"
        subtitle="Alternate spellings imports resolve through — not names the site shows"
        flush
      >
        {aliases.loading ? (
          <div style={{ padding: 24 }}>
            <Loading rows={5} />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 24 }}>
            <EmptyState title="No aliases recorded">
              Aliases appear here when a merge folds one identity into another, or when an import
              is told that a workbook name belongs to an existing player.
            </EmptyState>
          </div>
        ) : (
          <AliasTable
            rows={rows}
            onRemove={removal => {
              setRemoved(null)
              setPending(removal)
            }}
          />
        )}
      </Card>
    </div>
  )
}
